/** @jsxImportSource @emotion/react */
import { PageWrapper } from "../../components/page-skeleton/PageWrapper";
import { CardContainer } from "../../components/page-skeleton/CardContainer";
import { PageHeader } from "../../components/page-skeleton/PageHeader";
import { useSelector } from "react-redux";
import * as authSelectors from "../auth/authSelectors";
import React from "react";
import _ from "lodash";
import { SpinnerBlock } from "../common/Spinner";
import { ProductsTable } from "./ProductsTable";
import { css } from "@emotion/react";
import { SearchTextFieldWithKeyboardInteraction } from "../../components/forms/fields/SearchTextFieldWithKeyboardInteraction";
import { CardBody } from "../../components/page-skeleton/CardBody";
import { breakpoints } from "../../components/breakpoints";
import { CreateProductSelector } from "./components/CreateProductSelector";
import { useListProducts_ } from "./service/products-api";
import { EmptyState } from "../../components/EmptyState";
import { Divider } from "../../components/Divider";
import { useHandleFetchError } from "../../error-handler";
import { useSearchParamsManipulation } from "../../hooks/use-searchparams-manipulation";
import { ProductsFilter } from "./ProductsFilter";
import { ProductListItem } from "./models/product";
import { ButtonPrimary } from "../../components/NewButtons";
import { ActionSpinner } from "../../components/ActionSpinner";
import { DownloadAsCSVButton } from "../../components/DownloadAsCSV";

export const localStorageScrollPositionName = "product-list-scroll-position";

const textFieldStyling = css`
  width: 350px;
  ${breakpoints.tablet} {
    width: 100%;
  }
`;

const cardBodyAdditionalStyling = css`
  padding-bottom: 24px;
`;

const loadMoreButtonAdditionalStyling = css`
  padding-bottom: 24px;
  padding-top: 24px;
`;

export const ProductsList = () => {
  const {
    searchValue,
    setSearchValue,
    removeSearchValue,
    categories,
    tags,
    kitchenCategories,
    currency,
    country,
  } = useSearchParamsManipulation();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useListProducts_(organizationKeyName, {
    classification: "admin-main-list",
    tags: tags?.split(",") || [],
    productCategory: categories?.split(",") || [],
    kitchenCategory: kitchenCategories?.split(",") || [],
    currencies: currency?.split(",") || [],
    countryAlpha2Codes: country?.split(",") || [],
    ...(searchValue && { name: searchValue }),
  });
  /* const sortedProducts = useNaturalSortByName(data); */

  React.useEffect(() => {
    if (localStorage) {
      const scrollPosition = localStorage.getItem(
        localStorageScrollPositionName
      );
      if (scrollPosition) {
        // Need timeout in order for it to fire. Without sometimes it fires before render, where nothing happens.
        setTimeout(() => {
          window.scrollTo({
            top: Number(scrollPosition),
            behavior: "smooth",
          });
        }, 700);
      }
    }
  }, []);

  const debouncedSetSearchValue = _.debounce(setSearchValue, 400);

  useHandleFetchError(error);

  // Cancelling the debounced functions when the component unmounts
  React.useEffect(() => {
    return () => {
      debouncedSetSearchValue.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let products: ProductListItem[] = [];
  if (data?.pages) {
    data.pages.forEach((p) =>
      p.data
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach((p) => products.push(p))
    );
  }

  return (
    <PageWrapper>
      <CardContainer>
        <PageHeader
          title="Product list"
          subtitle="Create, edit and manage all products for your organization"
        >
          <div css={textFieldStyling}>
            <SearchTextFieldWithKeyboardInteraction
              searchValue={searchValue || ""}
              isDefaultValues={true}
              setSearchValue={(value) => {
                debouncedSetSearchValue.cancel();
                if (value === "") {
                  removeSearchValue();
                } else {
                  debouncedSetSearchValue(value);
                }
              }}
              placeholder="Search for product..."
            />
          </div>
          <DownloadAsCSVButton
            height="auto"
            data={products.map((p) => {
              const { _type, id, ...rest } = p;
              return rest;
            })}
            fileName="Product list"
          >
            Download as CSV
          </DownloadAsCSVButton>
          <CreateProductSelector />
        </PageHeader>
        <div>
          <Divider />
          <ProductsFilter
            additionalStyling={css`
              padding: 16px 24px;
            `}
          />
          {isLoading ? (
            <SpinnerBlock />
          ) : (
            <>
              {data?.pages && data?.pages.length > 0 ? (
                <>
                  <ProductsTable products={products} />
                  {hasNextPage && (
                    <CardBody
                      additionalStyles={loadMoreButtonAdditionalStyling}
                    >
                      <ButtonPrimary
                        width="100%"
                        disabled={isFetchingNextPage}
                        onClick={async () => {
                          await fetchNextPage();
                        }}
                      >
                        {isFetchingNextPage ? <ActionSpinner /> : "Load more"}
                      </ButtonPrimary>
                    </CardBody>
                  )}
                </>
              ) : (
                <>
                  <Divider />
                  <CardBody additionalStyles={cardBodyAdditionalStyling}>
                    <EmptyState
                      title="Click to create your first product"
                      subtitle="No products are currently created"
                    >
                      <CreateProductSelector />
                    </EmptyState>
                  </CardBody>
                </>
              )}
            </>
          )}
        </div>
      </CardContainer>
    </PageWrapper>
  );
};
